import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { supabase } from '../lib/supabase';
import { toast } from 'sonner';
import { Home } from 'lucide-react';

interface GuestLoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void; // Optional callback after successful login
}

export function GuestLoginModal({
  isOpen,
  onClose,
  onSuccess,
}: GuestLoginModalProps) {
  const { t } = useTranslation();

  // Listen for auth state changes to detect successful login
  useEffect(() => {
    if (!isOpen) return;

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN' && session) {
        // User successfully logged in
        onSuccess?.();
        onClose();
        toast.success('Successfully signed in!');
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [isOpen, onClose, onSuccess]);

  const handleGoogleLogin = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
    });
    if (error) {
      console.error('Error logging in:', error.message);
      toast.error(error.message || 'Failed to sign in with Google');
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="!max-w-[calc(100vw-32px)] !w-[calc(100vw-32px)] sm:!max-w-md sm:!w-full !rounded-3xl !p-0 !gap-0 overflow-hidden [&>button[data-slot='dialog-close']]:hidden">
        {/* Close button - positioned absolutely */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-20 p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
          aria-label="Close"
        >
          <svg className="w-5 h-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Header with logo */}
        <div className="pt-8 pb-6 px-6 text-center">
          <div className="flex justify-center mb-4">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-600 to-purple-600 rounded-2xl shadow-lg">
              <Home className="w-8 h-8 text-white" />
            </div>
          </div>
          <DialogTitle className="text-2xl font-bold text-gray-900 mb-2">
            {t('loginModal.title', 'Welcome to PadSwipe')}
          </DialogTitle>
          <p className="text-sm text-gray-600 leading-relaxed">
            {t('loginModal.description', 'Sign in to save your favorites and continue your search')}
          </p>
        </div>

        {/* Main content */}
        <div className="px-6 pb-6">
          <Button
            onClick={handleGoogleLogin}
            className="w-full h-14 rounded-2xl bg-white border-2 border-gray-200 hover:border-gray-300 hover:bg-gray-50 text-gray-900 font-medium text-base shadow-sm transition-all"
          >
            <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            {t('login.continueWithGoogle')}
          </Button>
        </div>

        {/* Terms footer */}
        <div className="px-6 pb-6 pt-2">
          <p className="text-center text-xs text-gray-500 leading-relaxed">
            {t('login.terms')}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

